import React from "react";
import API from "@beardeddevops/react.api";
import Types from "constants/Types";
import Framework from "@beardeddevops/react.framework";
import {Button, ButtonGroup} from "reactstrap";
import Card from "@material-ui/core/Card";
import CardContent from "@material-ui/core/CardContent";
import Tabs from "@material-ui/core/Tabs";
import Tab from "@material-ui/core/Tab";
import TextField from "@material-ui/core/TextField";
import Select from "react-select";
import {LineChart} from "react-chartkick";
import Chart from "chart.js";
import moment from "moment-timezone";
import UserRoles from "constants/UserRoles";
import HelpDesksContacted from "components/Objects/HelpDesksContacted";
import {registerFeContactLinePlugin} from "components/Collections/feContactLinePlugin";
import {regionRankingValue} from "constants/AppointmentEta";

const CATEGORIES = [
	{id: "low_dollar_average", label: "Low Dollar Average", unit: "$"},
	{id: "av_conversion", label: "AV Conversion", unit: "%"},
	{id: "low_reviews", label: "Reviews", unit: "%"},
	{id: "high_four_point_waived", label: "Four-Point Waived", unit: "%"},
	{id: "low_cards", label: "Cards Left", unit: "%"},
];

const PRESETS = [
	{id: "1d", label: "1d", days: 1},
	{id: "7d", label: "7d", days: 7},
	{id: "30d", label: "30d", days: 30},
];

const PHOENIX = "America/Phoenix";
const CHART_COLORS = ["#FF0000", "#2B81C2", "#5D16EE", "#EE16B1", "#B1EE16", "#EE5316", "#EEBF16", "#2B36C2", "#B72BC2", "#4C14B1", "#B14C14", "#14B14C", "#B19514", "#0f3f04", "#bcf778", "#032335", "#faa3ca", "#ae0a7f", "#875008"];

const phoenixToday = () => moment().tz(PHOENIX);

const parsePhoenixDate = (value) => {
	const raw = String(value || "").slice(0, 10);
	const parsed = moment.tz(raw, "YYYY-MM-DD", true, PHOENIX);
	return parsed.isValid() ? parsed : null;
};

const formatChartDate = (value) => {
	const parsed = parsePhoenixDate(value);
	return parsed ? parsed.format("ddd MMM D") : String(value || "");
};

const formatCallWhen = (value) => {
	const parsed = moment(value).tz(PHOENIX);
	return parsed.isValid() ? parsed.format("ddd MMM D h:mm A") : "";
};

const rangeForPreset = (id, today = phoenixToday()) => {
	const end = today.clone().startOf("day");
	if (id === "1d") {
		const day = end.format("YYYY-MM-DD");
		return {period_start: day, period_end: day};
	}
	const days = id === "30d" ? 29 : 6;
	return {
		period_start: end.clone().subtract(days, "days").format("YYYY-MM-DD"),
		period_end: end.format("YYYY-MM-DD"),
	};
};

const weekRange = (today = phoenixToday()) => {
	const start = today.clone().startOf("week");
	return {
		period_start: start.format("YYYY-MM-DD"),
		period_end: start.clone().add(6, "days").format("YYYY-MM-DD"),
	};
};

const matchingPreset = (start, end) => {
	return PRESETS.find((preset) => {
		const range = rangeForPreset(preset.id);
		return range.period_start === start && range.period_end === end;
	})?.id || null;
};

const categoryHeading = (category) => (
	category.unit === "%" ? `${category.label} (%)` : category.label
);

const formatValue = (category, value) => {
	const num = Number(value || 0);
	if (category === "low_dollar_average") {
		return `$${Math.round(num)}`;
	}
	return `${num.toFixed(1)}%`;
};

const metricValue = (point, category) => {
	const metric = point ? point[category] : null;
	if (metric == null || typeof metric !== "object") {
		const raw = Number(metric);
		return Number.isFinite(raw) ? raw : 0;
	}
	const value = Number(metric.value);
	return Number.isFinite(value) ? value : 0;
};

const rangeDays = (start, end) => {
	const from = parsePhoenixDate(start);
	const to = parsePhoenixDate(end);
	if (!from || !to) {
		return 0;
	}
	return to.diff(from, "days") + 1;
};

const rollMetric = (points, category, uniqueWeeks) => {
	let numerator = 0;
	let denominator = 0;
	const seen = new Set();
	(points || []).forEach((point) => {
		if (uniqueWeeks) {
			const week = point.week_start || point.date;
			if (seen.has(week)) {
				return;
			}
			seen.add(week);
		}
		const metric = point[category] || {};
		numerator += Number(metric.numerator || 0);
		denominator += Number(metric.denominator || 0);
	});
	const isRate = category !== "low_dollar_average";
	return {
		numerator,
		denominator,
		value: denominator > 0 ? (isRate ? (numerator / denominator) * 100 : numerator / denominator) : 0,
	};
};

const collapsePointsForChart = (points, start, end) => {
	const list = points || [];
	const days = rangeDays(start, end);
	if (days <= 45 || list.length <= 45) {
		return list;
	}
	const useMonth = days > 180;
	const groups = new Map();
	list.forEach((point) => {
		const key = useMonth ? String(point.date || "").slice(0, 7) : (point.week_start || point.date);
		if (!key) {
			return;
		}
		if (!groups.has(key)) {
			groups.set(key, []);
		}
		groups.get(key).push(point);
	});
	return [...groups.entries()].map(([key, group]) => {
		const first = group[0];
		const rolled = {
			date: useMonth ? `${key}-01` : (first.week_start || first.date),
			week_start: first.week_start || first.date,
		};
		CATEGORIES.forEach((category) => {
			rolled[category.id] = rollMetric(group, category.id, category.id === "low_reviews");
		});
		const hasWork = CATEGORIES.some((category) => Number(rolled[category.id].denominator) > 0);
		return hasWork ? rolled : null;
	}).filter(Boolean);
};

const feFromSearch = (search) => {
	const raw = new URLSearchParams(search || "").get("fe");
	const fe = raw ? Number(raw) : NaN;
	return Number.isFinite(fe) && fe > 0 ? fe : null;
};

const parseListPayload = (data) => {
	if (Array.isArray(data)) {
		return data;
	}
	if (Array.isArray(data?.items)) {
		return data.items;
	}
	if (Array.isArray(data?.response?.items)) {
		return data.response.items;
	}
	const parsed = Object.keys(data || {}).map((key) => data[key]);
	if (Array.isArray(parsed[3])) {
		return parsed[3];
	}
	return parsed.filter((item) => item && typeof item === "object" && !Array.isArray(item) && (item.pkey || item.value));
};

const parseSeriesPayload = (data) => {
	const payload = data?.items ? data : (data?.response ?? data);
	let items = [];
	if (Array.isArray(payload)) {
		items = payload;
	} else if (Array.isArray(payload?.items)) {
		items = payload.items;
	} else if (Array.isArray(payload?.items?.items)) {
		items = payload.items.items;
	}
	return {
		items,
		period_start: payload?.period_start || data?.period_start || null,
		period_end: payload?.period_end || data?.period_end || null,
	};
};

const engineerOption = (item) => {
	const region = item.regions || {};
	const ranking = region.ranking === null || region.ranking === undefined || region.ranking === ""
		? 100
		: Number(region.ranking);
	const pkey = Number(item.pkey);
	return {
		value: pkey,
		label: `${item.name_first || ""} ${item.name_last || ""}`.trim() || `FE ${pkey}`,
		region: item.region || region.pkey || null,
		regionName: region.name || item.region_name || "No Region",
		ranking: Number.isFinite(ranking) ? ranking : 100,
	};
};

const buildGroupedFeOptions = (fes) => {
	const groups = new Map();
	(fes || []).forEach((fe) => {
		const option = engineerOption(fe);
		const groupLabel = option.regionName;
		if (!groups.has(groupLabel)) {
			groups.set(groupLabel, {ranking: option.ranking, options: []});
		}
		groups.get(groupLabel).options.push(option);
	});
	return [...groups.entries()]
		.sort((a, b) => a[1].ranking - b[1].ranking || a[0].localeCompare(b[0]))
		.map(([label, group]) => {
			group.options.sort((a, b) => a.label.localeCompare(b.label));
			return {label, options: group.options};
		});
};

const flattenOptions = (grouped) => (grouped || []).flatMap((group) => group.options || []);

const clampRange = (start, end, which) => {
	if (!start || !end) {
		return {period_start: start, period_end: end};
	}
	if (start > end) {
		return which === "start"
			? {period_start: start, period_end: start}
			: {period_start: end, period_end: end};
	}
	const startDt = parsePhoenixDate(start);
	const endDt = parsePhoenixDate(end);
	if (startDt && endDt && endDt.diff(startDt, "days") > 366) {
		return {
			period_start: start,
			period_end: startDt.clone().add(366, "days").format("YYYY-MM-DD"),
		};
	}
	return {period_start: start, period_end: end};
};

export default class FePerformance extends React.Component {
	constructor(props) {
		super(props);
		moment.tz.setDefault(PHOENIX);
		const params = new URLSearchParams(props.history?.location?.search || "");
		const today = phoenixToday();
		const intervalParam = params.get("interval");
		const feParam = feFromSearch(props.history?.location?.search || "");
		let range;
		if (params.get("start") && params.get("end")) {
			range = clampRange(params.get("start"), params.get("end"), "start");
		} else if (intervalParam === "week") {
			range = weekRange(today);
		} else if (PRESETS.some((item) => item.id === intervalParam)) {
			range = rangeForPreset(intervalParam, today);
		} else {
			range = rangeForPreset("7d", today);
		}
		this.state = {
			period_start: range.period_start,
			period_end: range.period_end,
			tab: "graphs",
			selectedRegions: [],
			selectedUsers: feParam ? [{value: feParam, label: `FE ${feParam}`}] : [],
			regionOptions: [],
			userOptions: [],
			userOptionsFlat: [],
			items: [],
			thresholds: {},
			highlightFe: feParam,
			loading: false,
			logCallOpen: false,
			logCallEngineer: null,
		};
		this._search = props.history?.location?.search || "";
	}

	componentDidMount() {
		registerFeContactLinePlugin(Chart);
		this.loadRegions();
		this.loadEngineers();
		this.loadSeries();
	}

	componentDidUpdate() {
		const search = this.props.history?.location?.search || "";
		if (search !== this._search) {
			this._search = search;
			this.applyLocationSearch(search);
		}
	}

	applyLocationSearch = (search) => {
		const params = new URLSearchParams(search || "");
		const intervalParam = params.get("interval");
		const feParam = feFromSearch(search);
		const today = phoenixToday();
		let range;
		if (params.get("start") && params.get("end")) {
			range = clampRange(params.get("start"), params.get("end"), "start");
		} else if (intervalParam === "week") {
			range = weekRange(today);
		} else if (PRESETS.some((item) => item.id === intervalParam)) {
			range = rangeForPreset(intervalParam, today);
		} else {
			range = {
				period_start: this.state.period_start,
				period_end: this.state.period_end,
			};
		}
		const next = {
			period_start: range.period_start,
			period_end: range.period_end,
		};
		if (feParam) {
			const existing = (this.state.userOptionsFlat || []).find((item) => Number(item.value) === feParam);
			next.selectedUsers = [{value: feParam, label: existing?.label || `FE ${feParam}`, region: existing?.region}];
			next.highlightFe = feParam;
		} else {
			next.selectedUsers = [];
			next.highlightFe = null;
		}
		this.setState(next, this.loadSeries);
	};

	canAct = () => {
		const roles = this.props.roles || [];
		return roles.some((role) => [UserRoles.Admin, UserRoles.HelpDesk].includes(role));
	};

	loadRegions = async () => {
		try {
			const model = new API.BuildClass(Types.Regions._name, API.RequestType.READ_ALL);
			model.limit = 500;
			model.filter = {filterMetroWalk: true};
			let regions = parseListPayload(await model.submit(null, "GET_ALL"));
			regions = (regions || [])
				.filter((item) => item && (item.pkey || item.value))
				.sort((a, b) =>
					regionRankingValue(a) - regionRankingValue(b) ||
					(a.name || "").localeCompare(b.name || "")
				);
			this.setState({
				regionOptions: regions.map((item) => ({
					value: item.pkey || item.value,
					label: item.name || item.label,
					ranking: regionRankingValue(item),
				})),
			});
		} catch (e) {
			const fallback = [...(this.props.regions || [])]
				.sort((a, b) =>
					regionRankingValue(a) - regionRankingValue(b) ||
					(a.name || a.label || "").localeCompare(b.name || b.label || "")
				)
				.map((item) => ({
					value: item.pkey || item.value,
					label: item.name || item.label,
					ranking: regionRankingValue(item),
				}));
			this.setState({regionOptions: fallback});
		}
	};

	loadEngineers = async () => {
		try {
			const model = new API.BuildClass(Types.Users._name, API.RequestType.READ);
			model.limit = null;
			model.sort = "ranking";
			const users = parseListPayload(await model.submit(null, "GET_FIELD_ENGINEERS"));
			const grouped = buildGroupedFeOptions(users || []);
			const flat = flattenOptions(grouped);
			this.setState((prev) => {
				const byValue = new Map(
					(prev.selectedUsers || []).map((selected) => {
						const match = flat.find((item) => Number(item.value) === Number(selected.value));
						return [Number(selected.value), match || selected];
					})
				);
				(prev.selectedRegions || []).forEach((region) => {
					flat.filter((item) => Number(item.region) === Number(region.value)).forEach((option) => {
						byValue.set(Number(option.value), option);
					});
				});
				const urlFe = feFromSearch(this.props.history?.location?.search || this._search);
				if (urlFe && !byValue.has(urlFe)) {
					const match = flat.find((item) => Number(item.value) === urlFe);
					byValue.set(urlFe, match || {value: urlFe, label: `FE ${urlFe}`});
				} else if (urlFe && byValue.has(urlFe)) {
					const match = flat.find((item) => Number(item.value) === urlFe);
					if (match) {
						byValue.set(urlFe, match);
					}
				}
				return {
					userOptions: grouped,
					userOptionsFlat: flat,
					selectedUsers: [...byValue.values()],
					highlightFe: urlFe || prev.highlightFe,
				};
			}, () => {
				if ((this.state.selectedUsers || []).length) {
					this.loadSeries();
				}
			});
		} catch (e) {
			// Series rows still populate engineer names.
		}
	};

	loadSeries = async () => {
		const selectedUsers = this.state.selectedUsers || [];
		if (!selectedUsers.length) {
			this.setState({loading: false, items: [], thresholds: {}});
			return;
		}
		this.setState({loading: true});
		try {
			const model = new API.BuildClass(Types.Fe_Metrics._name, "GET_SERIES");
			model.filter = {
				period_start: this.state.period_start,
				period_end: this.state.period_end,
				filterUsers: selectedUsers.map((item) => Number(item.value)).filter((value) => Number.isFinite(value) && value > 0),
			};
			const data = await model.submit(null, "GET_SERIES");
			const parsed = parseSeriesPayload(data);
			const items = parsed.items;
			this.setState({
				loading: false,
				items,
				thresholds: items[0]?.thresholds || {},
				period_start: parsed.period_start || this.state.period_start,
				period_end: parsed.period_end || this.state.period_end,
			});
		} catch (e) {
			this.setState({loading: false, items: []});
		}
	};

	engineersInRegion = (regionId) => {
		return (this.state.userOptionsFlat || []).filter((item) => Number(item.region) === Number(regionId));
	};

	onRegionsChange = (selected) => {
		const next = selected || [];
		const prev = this.state.selectedRegions || [];
		const prevIds = new Set(prev.map((item) => Number(item.value)));
		const nextIds = new Set(next.map((item) => Number(item.value)));
		const added = next.filter((item) => !prevIds.has(Number(item.value)));
		const removedIds = [...prevIds].filter((id) => !nextIds.has(id));
		const byValue = new Map((this.state.selectedUsers || []).map((item) => [Number(item.value), item]));
		added.forEach((region) => {
			this.engineersInRegion(region.value).forEach((option) => {
				byValue.set(Number(option.value), option);
			});
		});
		removedIds.forEach((id) => {
			[...byValue.values()].forEach((option) => {
				if (Number(option.region) === Number(id)) {
					byValue.delete(Number(option.value));
				}
			});
		});
		this.setState({selectedRegions: next, selectedUsers: [...byValue.values()]}, this.loadSeries);
	};

	onEngineersChange = (selected) => {
		this.setState({selectedUsers: selected || []}, this.loadSeries);
	};

	setPreset = (id) => {
		this.setState({...rangeForPreset(id), items: [], loading: true}, this.loadSeries);
	};

	setRangeField = (field, value) => {
		const nextStart = field === "period_start" ? value : this.state.period_start;
		const nextEnd = field === "period_end" ? value : this.state.period_end;
		this.setState(clampRange(nextStart, nextEnd, field === "period_start" ? "start" : "end"), this.loadSeries);
	};

	activeSnooze = (row, category) => {
		const now = moment().tz(PHOENIX);
		return (row.snoozes || []).find((item) => {
			if (item.category !== category) {
				return false;
			}
			const starts = moment(item.starts_at);
			const ends = moment(item.ends_at);
			return starts.isValid() && ends.isValid() && !starts.isAfter(now) && !ends.isBefore(now);
		});
	};

	openLogCall = (row) => {
		this.setState({
			logCallOpen: true,
			logCallEngineer: {
				engineer_pkey: row.field_engineer,
				engineer_name: row.engineer_name,
				notes: "",
			},
		});
	};

	closeLogCall = () => {
		this.setState({logCallOpen: false, logCallEngineer: null});
	};

	afterLogCall = () => {
		this.closeLogCall();
		this.loadSeries();
	};

	contactMarkers = () => {
		const markers = [];
		(this.state.items || []).forEach((row) => {
			(row.contacts || []).forEach((contact) => {
				markers.push({
					...contact,
					engineer_name: row.engineer_name,
				});
			});
		});
		return markers;
	};

	allSnoozes = () => {
		const rows = [];
		(this.state.items || []).forEach((row) => {
			(row.snoozes || []).forEach((snooze) => {
				rows.push({
					...snooze,
					engineer_name: row.engineer_name,
				});
			});
		});
		return rows.sort((a, b) => moment(b.starts_at).valueOf() - moment(a.starts_at).valueOf());
	};

	snooze = async (fe, category) => {
		const model = new API.BuildClass(Types.Fe_Metrics._name, "POST_SNOOZE");
		model.object = {field_engineer: fe, category};
		await model.submit(null, "POST_SNOOZE");
		await this.loadSeries();
	};

	chartData = (category) => {
		const start = this.state.period_start;
		const end = this.state.period_end;
		const series = (this.state.items || []).map((row) => {
			const data = {};
			collapsePointsForChart(row.points, start, end).forEach((point) => {
				data[point.date] = metricValue(point, category);
			});
			return {name: row.engineer_name, data};
		});
		const threshold = this.state.thresholds?.[category]?.amount;
		if (threshold == null || threshold === "") {
			return series;
		}
		const floorData = {};
		collapsePointsForChart(this.state.items[0]?.points, start, end).forEach((point) => {
			floorData[point.date] = Number(threshold);
		});
		return [{name: "Floor", data: floorData}, ...series];
	};

	chartLibrary = (category) => ({
		feContactMarkers: this.contactMarkers(),
		legend: {display: false},
		maintainAspectRatio: false,
		spanGaps: true,
		elements: {
			line: {tension: 0, borderWidth: 1.5, fill: false},
			point: {radius: 2, hoverRadius: 4, hitRadius: 8},
		},
		tooltips: {
			mode: "nearest",
			intersect: true,
			callbacks: {
				title: (items) => formatChartDate(items?.[0]?.xLabel || items?.[0]?.label),
			},
		},
		scales: {
			xAxes: [{
				ticks: {
					maxTicksLimit: 8,
					autoSkip: true,
					callback: (value) => formatChartDate(value),
				},
			}],
			yAxes: [{
				ticks: category?.unit === "%"
					? {beginAtZero: true, suggestedMax: 100, maxTicksLimit: 6}
					: {beginAtZero: true, maxTicksLimit: 6},
			}],
		},
	});

	chartLegend = (category) => (
		<div style={styles.legend}>
			{this.chartData(category).map((item, index) => (
				<span key={item.name} style={styles.legendItem}>
					<span style={{...styles.legendSwatch, backgroundColor: CHART_COLORS[index % CHART_COLORS.length]}}/>
					{item.name}
				</span>
			))}
		</div>
	);

	render() {
		const {items, loading, tab, period_start, period_end} = this.state;
		const canAct = this.canAct();
		const activePreset = matchingPreset(period_start, period_end);
		const hasEngineers = (this.state.selectedUsers || []).length > 0;
		return (
			<Card>
				<CardContent>
					<div style={styles.toolbar}>
						<div style={styles.selectFill}>
							<Select
								isMulti
								placeholder="Regions"
								options={this.state.regionOptions}
								value={this.state.selectedRegions}
								onChange={this.onRegionsChange}
							/>
						</div>
						<div style={styles.selectFill}>
							<Select
								isMulti
								placeholder="Engineers"
								options={this.state.userOptions}
								value={this.state.selectedUsers}
								onChange={this.onEngineersChange}
							/>
						</div>
						<TextField
							type="date"
							label="Start Date"
							value={period_start}
							onChange={(event) => this.setRangeField("period_start", event.target.value)}
							InputLabelProps={{shrink: true}}
							inputProps={{max: period_end}}
						/>
						<TextField
							type="date"
							label="End Date"
							value={period_end}
							onChange={(event) => this.setRangeField("period_end", event.target.value)}
							InputLabelProps={{shrink: true}}
							inputProps={{min: period_start}}
						/>
						<ButtonGroup>
							{PRESETS.map((item) => (
								<Button
									key={item.id}
									color="info"
									outline={activePreset !== item.id}
									onClick={() => this.setPreset(item.id)}
								>
									{item.label}
								</Button>
							))}
						</ButtonGroup>
					</div>
					<Tabs value={tab} onChange={(event, value) => this.setState({tab: value})}>
						<Tab label="Data" value="data"/>
						<Tab label="Graphs" value="graphs"/>
					</Tabs>
					{tab === "data" && (
						<div className="table-responsive mt-3">
							<table className="table table-sm table-striped">
								<thead>
								<tr>
									<th>Engineer</th>
									{CATEGORIES.map((category) => <th key={category.id}>{categoryHeading(category)}</th>)}
									{canAct ? <th></th> : null}
								</tr>
								</thead>
								<tbody>
								{(items || []).map((row) => (
									<tr key={row.field_engineer} style={this.state.highlightFe === row.field_engineer ? {background: "#fff3cd"} : null}>
										<td>{row.engineer_name}</td>
										{CATEGORIES.map((category) => (
											<td key={category.id}>{formatValue(category.id, row.summary?.[category.id]?.value)}</td>
										))}
										{canAct ? (
											<td>
												{CATEGORIES.filter((category) => category.id !== "low_dollar_average").map((category) => {
													const snooze = this.activeSnooze(row, category.id);
													return (
														<Button
															key={category.id}
															size="sm"
															color="secondary"
															className="mr-1 mb-1"
															disabled={!!snooze}
															onClick={() => this.snooze(row.field_engineer, category.id)}
														>
															{snooze ? `Snoozed ${category.label.split(" ")[0]}` : `Snooze ${category.label.split(" ")[0]}`}
														</Button>
													);
												})}
												<Button size="sm" color="primary" className="mb-1" onClick={() => this.openLogCall(row)}>
													Log Call
												</Button>
											</td>
										) : null}
									</tr>
								))}
								</tbody>
							</table>
							{!loading && !hasEngineers && <p>Select engineers to load metrics.</p>}
							{!loading && hasEngineers && (!items || items.length < 1) && <p>No engineers in this filter.</p>}
							{this.allSnoozes().length > 0 && (
								<div className="mt-4">
									<h6>Snooze history</h6>
									<table className="table table-sm">
										<thead>
										<tr>
											<th>Engineer</th>
											<th>Category</th>
											<th>Starts</th>
											<th>Ends</th>
											<th>By</th>
										</tr>
										</thead>
										<tbody>
										{this.allSnoozes().map((snooze) => (
											<tr key={snooze.pkey}>
												<td>{snooze.engineer_name}</td>
												<td>{CATEGORIES.find((item) => item.id === snooze.category)?.label || snooze.category}</td>
												<td>{formatCallWhen(snooze.starts_at)}</td>
												<td>{formatCallWhen(snooze.ends_at)}</td>
												<td>{snooze.actor_name || "Admin"}</td>
											</tr>
										))}
										</tbody>
									</table>
								</div>
							)}
						</div>
					)}
					{tab === "graphs" && (
						<div style={styles.graphWrap}>
							{!hasEngineers && !loading && (
								<p className="mt-3">Select engineers to load graphs.</p>
							)}
							{hasEngineers && (
								<div style={styles.chartContainer}>
									{CATEGORIES.map((category) => (
										<div key={category.id} style={styles.chart}>
											<h3 style={styles.chartTitle}>{categoryHeading(category)}</h3>
											<LineChart
												key={`${category.id}-${period_start}-${period_end}`}
												data={this.chartData(category.id)}
												colors={CHART_COLORS}
												discrete
												curve={false}
												height="380px"
												library={this.chartLibrary(category)}
											/>
											{this.chartLegend(category.id)}
										</div>
									))}
								</div>
							)}
						</div>
					)}
					<Framework.Elements.LoadingSpinner isLoading={loading}/>
					<Framework.Elements.UniversalModal
						name={`Log Call - ${this.state.logCallEngineer?.engineer_name ?? "Field Engineer"}`}
						object={HelpDesksContacted}
						history={this.props.history}
						toggle={this.state.logCallOpen}
						reportClose={this.closeLogCall}
						afterSave={this.afterLogCall}
						objectProps={{
							current_note: this.state.logCallEngineer ?? {engineer_pkey: null, notes: ""},
							date_contacted: moment.tz(PHOENIX).format("YYYY-MM-DD"),
						}}
						hideGoTo={true}
					/>
				</CardContent>
			</Card>
		);
	}
}

const styles = {
	toolbar: {
		display: "flex",
		flexWrap: "wrap",
		alignItems: "flex-end",
		gap: 12,
		marginBottom: 12,
	},
	selectFill: {
		flex: "1 1 220px",
		minWidth: 0,
	},
	graphWrap: {
		marginTop: 8,
	},
	chartContainer: {
		display: "flex",
		flexWrap: "wrap",
		gap: 16,
		justifyContent: "space-between",
	},
	chart: {
		flex: "1 1 46%",
		minWidth: 360,
		maxWidth: "100%",
	},
	chartTitle: {
		textAlign: "center",
		marginBottom: 8,
		fontSize: 18,
		fontWeight: "bold",
	},
	legend: {
		display: "flex",
		flexWrap: "wrap",
		justifyContent: "center",
		gap: "6px 14px",
		marginTop: 8,
	},
	legendItem: {
		display: "inline-flex",
		alignItems: "center",
		gap: 6,
		fontSize: 13,
		lineHeight: 1.3,
	},
	legendSwatch: {
		width: 12,
		height: 12,
		flexShrink: 0,
	},
};
